import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

export default function VerifyPage() {
    const [user, setUser] = useState(null);
    const [certId, setCertId] = useState('');

    useEffect(() => {
        const uJson = localStorage.getItem('user');
        if (uJson) setUser(JSON.parse(uJson));
    }, []);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleVerify = async (e) => {
        e.preventDefault();
        if (!certId.trim()) return;
        setLoading(true);
        setResult(null);
        setError('');
        try {
            const res = await api.get(
              `/api/attestations/${encodeURIComponent(certId.trim())}`
            );
            setResult(res.data);
        } catch (err) {
            if (err.response?.status === 404) {
                setError('No certificate found with this ID on the ledger.');
            } else {
                setError('Verification failed. Please check the ID and try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const statusConfig = {
        active: { 
            label: 'VALID — Active Certificate', 
            color: 'text-emerald-400',
            border: 'border-emerald-500/20',
            bg: 'bg-emerald-500/5'
        },
        retired: { 
            label: 'RETIRED — ESG Claim Complete', 
            color: 'text-blue-400',
            border: 'border-blue-500/20',
            bg: 'bg-blue-500/5'
        },
        pending_expert: { 
            label: 'PENDING — Awaiting Verification', 
            color: 'text-yellow-400',
            border: 'border-yellow-500/20',
            bg: 'bg-yellow-500/5'
        },
        rejected: { 
            label: 'REJECTED', 
            color: 'text-rose-400',
            border: 'border-rose-500/20',
            bg: 'bg-rose-500/5'
        }
    };

    const cfg = result ? 
      (statusConfig[result.status] || statusConfig['pending_expert']) 
      : null;

    return (
        <div className="min-h-screen bg-[#080a0e] text-slate-300 
                        font-sans">
            {/* Background */}
            <div className="fixed inset-0 pointer-events-none 
                            overflow-hidden">
                <div className="absolute top-[-10%] left-[-10%] 
                                w-[40%] h-[40%] bg-emerald-900/10 
                                rounded-full blur-[120px]">
                </div>
                <div className="absolute bottom-[-10%] right-[-10%] 
                                w-[40%] h-[40%] bg-slate-800/20 
                                rounded-full blur-[120px]">
                </div>
            </div>

            {/* Nav */}
            <nav className="relative z-10 px-8 py-6 flex 
                            justify-between items-center 
                            max-w-4xl mx-auto border-b 
                            border-white/5">
                <Link to="/" 
                      className="flex items-center gap-3 
                                 hover:opacity-85 transition">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        <svg className="w-7 h-7 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 20L8 8L13 16" />
                            <path d="M8 20L15 6L22 20" />
                            <path d="M8 8L15 6" strokeDasharray="1.5 1.5" />
                            <circle cx="15" cy="6" r="1.5" fill="currentColor" />
                        </svg>
                    </div>
                    <div>
                        <p className="text-sm font-bold 
                                      text-white leading-none">
                            Silicon Himalayas Ledger
                        </p>
                        <p className="text-[9px] text-slate-500 
                                      uppercase tracking-widest 
                                      font-bold mt-0.5">
                            I-REC Certificate Registry
                        </p>
                    </div>
                </Link>
                {user ? (
                    <Link to={
                        user.role === 'admin' ? '/admin' 
                        : user.role === 'expert' ? '/dashboard/expert' 
                        : user.role === 'investor' ? '/dashboard/investor' 
                        : user.role === 'observer' ? '/dashboard/observer' 
                        : '/dashboard'
                    } className="text-xs font-bold uppercase tracking-widest text-emerald-400 hover:text-white transition flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                        Portal
                    </Link>
                ) : (
                    <Link to="/login" 
                          className="text-xs font-bold uppercase 
                                     tracking-widest text-slate-500 
                                     hover:text-white transition">
                        Sign In
                    </Link>
                )}
            </nav>

            <main className="relative z-10 max-w-4xl mx-auto 
                             px-6 pt-16 pb-32">

                {/* Header */}
                <div className="mb-12">
                    <p className="text-[10px] font-bold uppercase 
                                  tracking-widest text-emerald-500 
                                  mb-3">
                        Public Certificate Verification
                    </p>
                    <h1 className="text-4xl font-bold text-white 
                                   mb-4 tracking-tight">
                        Verify an I-REC Certificate
                    </h1>
                    <p className="text-slate-400 max-w-xl 
                                  leading-relaxed">
                        Enter a certificate ID to verify its 
                        authenticity, status, and generation data 
                        directly against the Hyperledger Fabric 
                        ledger. All records are immutable and 
                        publicly verifiable.
                    </p>
                </div>

                {/* Search Form */}
                <form onSubmit={handleVerify} 
                      className="mb-10">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="text"
                            value={certId}
                            onChange={e => setCertId(e.target.value)}
                            placeholder="Enter Certificate ID (e.g. ATT-2026-xxxxx)"
                            className="flex-1 bg-white/5 border 
                                       border-white/10 
                                       focus:border-emerald-500/50 
                                       focus:outline-none 
                                       focus:ring-1 
                                       focus:ring-emerald-500/20 
                                       rounded-xl px-5 py-4 
                                       text-white text-sm 
                                       placeholder:text-slate-600 
                                       transition"
                        />
                        <button
                            type="submit"
                            disabled={loading || !certId.trim()}
                            className="px-8 py-4 bg-emerald-600 
                                       hover:bg-emerald-500 
                                       disabled:opacity-40 
                                       disabled:cursor-not-allowed 
                                       text-white rounded-xl 
                                       font-bold text-sm 
                                       transition whitespace-nowrap"
                        >
                            {loading ? 'Verifying...' : 'Verify'}
                        </button>
                    </div>
                </form>

                {/* Error State */}
                {error && (
                    <div className="mb-8 p-5 bg-rose-500/5 
                                    border border-rose-500/20 
                                    rounded-xl">
                        <p className="text-rose-400 text-sm 
                                      font-bold">{error}</p>
                        <p className="text-slate-500 text-xs mt-1">
                            Certificate IDs are case-sensitive. 
                            Check the ID on your retirement 
                            certificate PDF.
                        </p>
                    </div>
                )}

                {/* Result */}
                {result && cfg && (
                    <div className="space-y-6">

                        {/* Status Banner */}
                        <div className={`p-6 rounded-xl border 
                                        ${cfg.border} ${cfg.bg}`}>
                            <div className="flex items-center 
                                            gap-3 mb-1">
                                <div className={`w-2.5 h-2.5 
                                                 rounded-full 
                                                 ${result.status === 'active' 
                                                   ? 'bg-emerald-400 animate-pulse' 
                                                   : result.status === 'retired' 
                                                     ? 'bg-blue-400' 
                                                     : result.status === 'rejected'
                                                       ? 'bg-rose-500'
                                                       : 'bg-yellow-400'}`}>
                                </div>
                                <p className={`font-bold text-sm 
                                              uppercase tracking-wider 
                                              ${cfg.color}`}>
                                    {cfg.label}
                                </p>
                            </div>
                            <p className="text-xs text-slate-500 
                                          font-mono ml-5">
                                Ledger ID: {result.id}
                            </p>
                        </div>

                        {/* Certificate Details */}
                        <div className="bg-white/3 border 
                                        border-white/8 rounded-xl 
                                        overflow-hidden">
                            <div className="px-6 py-4 border-b 
                                            border-white/5">
                                <p className="text-[10px] font-bold 
                                              uppercase tracking-widest 
                                              text-slate-500">
                                    Certificate Details
                                </p>
                            </div>
                            <div className="divide-y 
                                            divide-white/5">
                                {[
                                    { 
                                        label: 'Volume', 
                                        value: `${result.mwh} MWh`,
                                        highlight: true 
                                    },
                                    { 
                                        label: 'Standard', 
                                        value: 'I-REC(E)' 
                                    },
                                    { 
                                        label: 'Fuel Type', 
                                        value: result.fuelType 
                                                || 'Solar' 
                                    },
                                    { 
                                        label: 'Technology', 
                                        value: result.technologyType 
                                                || 'Photovoltaic' 
                                    },
                                    { 
                                        label: 'Vintage Period', 
                                        value: result.vintagePeriod 
                                               || new Date(
                                                    result.timestamp
                                                  ).toLocaleDateString(
                                                    'en-GB', 
                                                    { month: 'long', 
                                                      year: 'numeric' }
                                                  )
                                    },
                                    { 
                                        label: 'Source Plant', 
                                        value: result.plantName 
                                               || result.projectId 
                                               || '—' 
                                    },
                                    { 
                                        label: 'Issuer', 
                                        value: 'GCC (Green Certificate Company) · Evident Registry' 
                                    },
                                    { 
                                        label: 'Evident Serial', 
                                        value: result.evidentSerial 
                                               || 'Pending GCC/Evident Issuance',
                                        mono: true 
                                    },
                                    { 
                                        label: 'Committee Verified', 
                                        value: result.expertDid 
                                               ? `Yes · ${result.expertDid.substring(0,16)}...` 
                                               : 'Pending'
                                    },
                                    { 
                                        label: 'Issued', 
                                        value: new Date(
                                                 result.timestamp
                                               ).toLocaleString() 
                                    },
                                    result.retirementDate && { 
                                        label: 'Retired', 
                                        value: new Date(
                                                 result.retirementDate
                                               ).toLocaleString() 
                                    },
                                    result.retirementNotes && { 
                                        label: 'ESG Claim Purpose', 
                                        value: result.retirementNotes 
                                    }
                                ].filter(Boolean).map((row, i) => (
                                    <div key={i} 
                                         className="flex flex-col sm:flex-row sm:justify-between 
                                                    items-start px-6 
                                                    py-4 gap-1 sm:gap-6 w-full">
                                        <span className="text-xs 
                                                         font-bold 
                                                         uppercase 
                                                         tracking-widest 
                                                         text-slate-500 
                                                         sm:whitespace-nowrap 
                                                         mt-0.5">
                                            {row.label}
                                        </span>
                                        <span className={`text-sm 
                                                         sm:text-right 
                                                         ${row.highlight 
                                                           ? 'font-bold text-emerald-400 text-base' 
                                                           : row.mono 
                                                             ? 'font-mono text-slate-300 text-xs break-all text-left' 
                                                             : 'text-slate-200'}`}>
                                            {row.value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Blockchain Proof */}
                        <div className="bg-white/3 border 
                                        border-white/8 rounded-xl 
                                        px-6 py-5">
                            <p className="text-[10px] font-bold 
                                          uppercase tracking-widest 
                                          text-slate-500 mb-3">
                                Blockchain Record
                            </p>
                            <p className="text-[10px] font-mono 
                                          text-slate-400 
                                          break-all leading-relaxed">
                                Ledger: Hyperledger Fabric v2.5 · 
                                Channel: sh-green-finance-channel · 
                                Organisation: SiliconHimalayasMSP
                            </p>
                            <p className="text-[10px] text-slate-600 
                                          mt-2">
                                This record is cryptographically 
                                signed and immutable. Any tampering 
                                will be detectable on-chain.
                            </p>
                        </div>

                        {/* Sandbox Notice */}
                        <div className="bg-yellow-500/5 border 
                                        border-yellow-500/10 
                                        rounded-xl px-6 py-4">
                            <p className="text-[10px] font-bold 
                                          uppercase tracking-widest 
                                          text-yellow-600 mb-1">
                                NRB Sandbox Phase
                            </p>
                            <p className="text-[10px] text-slate-500">
                                This platform is operating under 
                                the NRB regulatory sandbox. 
                                Evident Registry serial numbers 
                                will be assigned following formal 
                                GCC/Evident I-REC issuance 
                                post-commissioning.
                            </p>
                        </div>
                    </div>
                )}

                {/* How to find your certificate ID */}
                {!result && !error && (
                    <div className="mt-12 p-6 bg-white/3 
                                    border border-white/8 
                                    rounded-xl">
                        <p className="text-xs font-bold uppercase 
                                      tracking-widest text-slate-500 
                                      mb-4">
                            Where to find a Certificate ID
                        </p>
                        <ul className="space-y-2 text-xs 
                                       text-slate-400">
                            <li>· On the PDF retirement certificate 
                                downloaded from the investor 
                                portal</li>
                            <li>· In the Live Asset Explorer on 
                                the platform homepage</li>
                            <li>· In your retirement confirmation 
                                email</li>
                            <li>· Format: begins with the 
                                attestation ID assigned at 
                                issuance</li>
                        </ul>
                    </div>
                )}
            </main>

            <footer className="relative z-10 border-t 
                               border-white/5 py-8 text-center">
                <p className="text-[9px] font-bold uppercase 
                               tracking-widest text-slate-700">
                    Silicon Himalayas Ledger · 
                    Hyperledger Fabric · 
                    I-REC Standard · 
                    GCC · Evident Registry
                </p>
            </footer>
        </div>
    );
}
